import { api } from '@/api';
import { MfaPreferenceInput, MfaToggleInput } from '@vivport/backend/types';
import { startAuthentication } from '@simplewebauthn/browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authKeys } from './useAuthQueries';

/**
 * MFA-related React Query mutations
 * Centralized mutations for MFA authentication flows
 */

/**
 * Send email MFA code mutation
 */
export const useSendEmailMfaCode = () => {
  return useMutation({
    mutationFn: (mfaToken: string) => api.auth.sendEmailMfaCode(mfaToken),
    onSuccess: () => {
      toast.success('Verification code sent to your email.');
    },
    onError: (error: Error) => {
      const errorMessage = error.message || 'Failed to send verification code.';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    },
  });
};

/**
 * Verify email MFA code mutation
 */
export const useVerifyEmailMfa = () => {
  return useMutation({
    mutationFn: ({ mfaToken, code }: { mfaToken: string; code: string }) => 
      api.auth.verifyEmailMfa({ mfaToken, code }),
    onError: (error: Error) => {
      const errorMessage = error.message || 'Failed to verify email code.';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    },
  });
};

/**
 * Get passkey MFA challenge mutation
 */
export const useGetPasskeyMfaChallenge = () => {
  return useMutation({
    mutationFn: (mfaToken: string) => api.auth.getPasskeyMfaChallenge(mfaToken),
    onError: (error: Error) => {
      const errorMessage = error.message || 'Failed to get passkey challenge.';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    },
  });
};

/**
 * Verify passkey MFA mutation
 */
export const useVerifyPasskeyMfa = () => {
  return useMutation({
    mutationFn: async ({ mfaToken }: { mfaToken: string }) => {
      // Get passkey challenge
      const challenge = await api.auth.getPasskeyMfaChallenge(mfaToken);

      // Perform WebAuthn authentication using SimpleWebAuthn helper
      // This properly handles all base64url decoding and ArrayBuffer conversions
      const assertion = await startAuthentication({ optionsJSON: challenge });

      // Verify the passkey
      return api.auth.verifyPasskeyMfa({
        mfaToken,
        assertion
      });
    },
    onError: (error: Error) => {
      const errorMessage = error.message || 'Passkey authentication failed.';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    },
  });
};

/**
 * Send password reset email mutation
 */
export const useSendPasswordResetEmail = () => {
  return useMutation({
    mutationFn: ({ email }: { email: string }) => api.auth.sendPasswordResetEmail({ email }),
    onSuccess: () => {
      toast.success('Password reset instructions sent to your email');
    },
    onError: (error: Error) => {
      const errorMessage = error.message || 'Failed to send password reset email.';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    },
  });
};

/**
 * Reset password mutation
 */
export const useResetPassword = () => {
  return useMutation({
    mutationFn: ({ passwordResetToken, newPassword }: { passwordResetToken: string; newPassword: string }) => 
      api.auth.resetPassword({ passwordResetToken, newPassword }),
    onSuccess: () => {
      toast.success('Password has been reset successfully');
    },
    onError: (error: Error) => {
      const errorMessage = error.message || 'Failed to reset password.';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    },
  });
};

/**
 * Check invite status mutation
 */
export const useCheckInviteStatus = (token: string) => {
  return useQuery({
    queryKey: ['invite-status', token],
    queryFn: () => api.auth.checkInviteStatus({ token }),
    enabled: !!token,
    retry: false,
  });
};

/**
 * Accept invite mutation
 */
export const useAcceptInvite = () => {
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password?: string }) => 
      api.auth.acceptInvite({ token, password }),
    onSuccess: () => {
      toast.success('Invitation accepted successfully!');
    },
    onError: (error: Error) => {
      const errorMessage = error.message || 'Error accepting invitation';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    },
  });
};

/**
 * Update MFA preference mutation
 */
export const useUpdateMfaPreference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: MfaPreferenceInput) => api.auth.updateMfaPreference(input),
    onSuccess: () => {
      // Invalidate user query to refetch with updated MFA preference
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
      toast.success('MFA preference updated');
    },
    onError: (error: Error) => {
      const errorMessage = error.message || 'Failed to update MFA preference';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    },
  });
};

/**
 * Toggle MFA on/off mutation
 */
export const useToggleMfa = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: MfaToggleInput) => api.auth.toggleMfa(input),
    onSuccess: () => {
      // Invalidate user query to refetch with updated MFA status
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
      toast.success('MFA settings updated');
    },
    onError: (error: Error) => {
      const errorMessage = error.message || 'Failed to update MFA settings';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    },
  });
};

/**
 * Register passkey mutation
 */
export const useRegisterPasskey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Get registration options
      const options = await api.auth.getPasskeyRegistrationOptions();
      
      // Perform WebAuthn registration using SimpleWebAuthn helper
      const { startRegistration } = await import('@simplewebauthn/browser');
      const credential = await startRegistration({ optionsJSON: options });
      
      // Verify the registration
      return api.auth.verifyPasskeyRegistration({ credential });
    },
    onSuccess: () => {
      // Invalidate user query to refetch with updated passkeys
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
      toast.success('Passkey registered successfully');
    },
    onError: (error: Error) => {
      const errorMessage = error.message || 'Failed to register passkey';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    },
  });
};

/**
 * Set selected member mutation
 * Updates localStorage and invalidates user query to refetch with new member context
 */
export const useSetMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (member: { id: number }) => {
      // Store selected member ID in localStorage
      localStorage.setItem('memberId', member.id.toString());
      // Return the member for consistency
      return member;
    },
    onSuccess: () => {
      // Invalidate user query to refetch with new member context
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });
};